import React from 'react';

import type { UserDto } from '../api/types';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchUsers } from '../store/thunks/fetchUsers';

export type UseUsersByIdResult = {
  /** Get user by telegramId, returns null if not found */
  getUser: (telegramId: string) => UserDto | null;
  /** Whether users are currently being fetched */
  isLoading: boolean;
};

// Global cache shared with useUserById
const checkedIdsGlobal = new Set<string>();
const loggedIdsGlobal = new Set<string>();
let fetchPromise: Promise<void> | null = null;

/**
 * Hook that finds multiple users by their telegramIds in the store.
 * If any user is not found, fetches users from server and checks again.
 * If still not found after fetch, returns null for that user and logs a warning (once per ID).
 *
 * @param telegramIds - Array of Telegram IDs to find
 * @returns Object with getter function and loading state
 */
export function useUsersById(telegramIds: string[]): UseUsersByIdResult {
  const dispatch = useAppDispatch();
  const users = useAppSelector((s) => s.entities.users.items);

  const [isLoading, setIsLoading] = React.useState(false);

  const usersMap = React.useMemo(() => {
    const map = new Map<string, UserDto>();
    for (const user of users) {
      map.set(user.telegramId, user);
    }
    return map;
  }, [users]);

  // IDs that are missing and haven't been checked yet
  const uncheckedMissingIds = React.useMemo(() => {
    return telegramIds.filter((id) => !usersMap.has(id) && !checkedIdsGlobal.has(id));
  }, [telegramIds, usersMap]);

  React.useEffect(() => {
    if (uncheckedMissingIds.length === 0) return;

    let cancelled = false;

    const fetchAndCheck = async () => {
      setIsLoading(true);

      // Reuse existing fetch promise to avoid duplicate requests
      if (!fetchPromise) {
        fetchPromise = dispatch(fetchUsers())
          .unwrap()
          .catch((error) => {
            console.error('[useUsersById] Failed to fetch users:', error);
          })
          .finally(() => {
            fetchPromise = null;
          });
      }

      await fetchPromise;

      if (!cancelled) {
        setIsLoading(false);
        for (const id of uncheckedMissingIds) {
          checkedIdsGlobal.add(id);
        }
      }
    };

    void fetchAndCheck();

    return () => {
      cancelled = true;
    };
  }, [uncheckedMissingIds, dispatch]);

  // Log warning once for users not found after fetch attempt
  React.useEffect(() => {
    if (isLoading) return;

    const stillMissing = telegramIds.filter(
      (id) => !usersMap.has(id) && checkedIdsGlobal.has(id) && !loggedIdsGlobal.has(id),
    );

    for (const id of stillMissing) {
      loggedIdsGlobal.add(id);
      console.warn(`[useUsersById] User with telegramId "${id}" not found after fetch`);
    }
  }, [telegramIds, usersMap, isLoading]);

  const getUser = React.useCallback(
    (telegramId: string): UserDto | null => usersMap.get(telegramId) ?? null,
    [usersMap],
  );

  return { getUser, isLoading };
}
